/**
 * Página de Produto V6 (aprimoramento progressivo).
 *
 * O formulário nativo é a base: sem JavaScript a compra funciona com a
 * variante pré-selecionada. Este custom element adiciona:
 * - troca de variante via Section Rendering API (?option_values=…&section_id=…),
 *   com AbortController, descarte de resposta antiga e atualização de URL
 *   por replaceState (sem entradas extras no histórico);
 * - sincronização em lugar de preço, SKU, disponibilidade, quantidade,
 *   estado do botão e opções — nenhum controle interativo é substituído,
 *   então o foco nunca se move;
 * - galeria aprimorada (item ativo + thumbnails no desktop, indicador de
 *   posição no mobile via IntersectionObserver — sem scroll listener);
 * - stepper de quantidade respeitando quantity_rule.
 *
 * Delegação de eventos no elemento raiz — nenhum listener por option value.
 */
(function () {
  'use strict';

  if (customElements.get('product-page')) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ProductPage extends HTMLElement {
    connectedCallback() {
      this.abortController = null;
      this.pendingKey = '';
      this.observer = null;

      this.onChange = this.handleChange.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);

      this.enhanceGallery();
      this.enhanceStickyBar();
      this.enhanceReveals();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      if (this.observer) this.observer.disconnect();
      if (this.stickyObserver) this.stickyObserver.disconnect();
      if (this.revealObserver) this.revealObserver.disconnect();
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
    }

    /* ------------------------------ galeria ----------------------------- */

    enhanceGallery() {
      var gallery = this.querySelector('[data-pdp-gallery]');
      if (!gallery) return;
      gallery.classList.add('pdp-gallery--enhanced');

      var thumbs = gallery.querySelector('[data-gallery-thumbs]');
      if (thumbs) thumbs.hidden = false;

      var items = this.galleryItems();
      var indicator = gallery.querySelector('[data-gallery-indicator]');
      if (indicator && items.length > 1) indicator.hidden = false;

      // Zoom em contexto: itens de imagem viram alvos focáveis/togglables
      var self2 = this;
      var hasZoom = false;
      items.forEach(function (item) {
        if (!item.dataset.zoomSrc) return;
        hasZoom = true;
        item.setAttribute('role', 'button');
        item.setAttribute('tabindex', '0');
        item.setAttribute('aria-label', self2.dataset.textZoom || '');
        item.setAttribute('aria-pressed', 'false');
        self2.bindZoom(item);
      });

      var zoomHint = gallery.querySelector('[data-gallery-zoom-hint]');
      if (zoomHint && hasZoom) zoomHint.hidden = false;

      // Indicador/thumb ativo no scroll-snap mobile, sem scroll listener
      if (items.length > 1 && 'IntersectionObserver' in window) {
        var self = this;
        this.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              var index = self.galleryItems().indexOf(entry.target);
              if (index >= 0) self.updateIndicator(index);
            });
          },
          { root: gallery.querySelector('[data-gallery-viewport]'), threshold: 0.6 }
        );
        items.forEach(function (item) {
          self.observer.observe(item);
        });
      }
    }

    galleryItems() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-gallery-item]'));
    }

    updateIndicator(index) {
      var items = this.galleryItems();
      var indicator = this.querySelector('[data-gallery-indicator]');
      if (indicator) indicator.textContent = index + 1 + ' / ' + items.length;
      var item = items[index];
      if (item && item.dataset.mediaId) this.markThumb(item.dataset.mediaId);
    }

    markThumb(mediaId) {
      var thumbs = this.querySelectorAll('[data-media-target]');
      Array.prototype.forEach.call(thumbs, function (thumb) {
        if (thumb.dataset.mediaTarget === String(mediaId)) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    setActiveMedia(mediaId, scroll) {
      var items = this.galleryItems();
      var target = null;
      items.forEach(function (item) {
        var isTarget = item.dataset.mediaId === String(mediaId);
        item.classList.toggle('is-active', isTarget);
        if (isTarget) target = item;
      });
      if (!target) return;

      this.markThumb(mediaId);
      this.updateIndicator(items.indexOf(target));

      if (scroll) {
        var viewport = this.querySelector('[data-gallery-viewport]');
        // Rolagem apenas dentro do viewport da galeria (modo scroll-snap)
        if (viewport && viewport.scrollWidth > viewport.clientWidth + 1) {
          viewport.scrollTo({
            left: target.offsetLeft - viewport.offsetLeft,
            behavior: reducedMotion.matches ? 'auto' : 'smooth'
          });
        }
      }
    }

    /* --------------------------- revelação suave ------------------------ */

    // Fade + translateY por bloco ao entrar na viewport (uma vez só).
    // Classes aplicadas apenas via JS: sem JS ou com prefers-reduced-motion
    // nada muda e todo o conteúdo fica visível de imediato.
    enhanceReveals() {
      if (reducedMotion.matches || !('IntersectionObserver' in window)) return;
      var targets = document.querySelectorAll(
        '.pdp-synergy, .pdp-about, .pdp-tech, .pdp-story > section, .pdp-reviews'
      );
      if (!targets.length) return;
      this.revealObserver = new IntersectionObserver(
        function (entries, observer) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('pdp-reveal--in');
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
      );
      var self = this;
      Array.prototype.forEach.call(targets, function (target) {
        target.classList.add('pdp-reveal');
        self.revealObserver.observe(target);
      });
    }

    /* --------------------------- barra sticky --------------------------- */

    // Barra mobile com preço + CTA: aparece somente quando o botão
    // principal saiu por CIMA da viewport (nunca antes de o shopper vê-lo)
    enhanceStickyBar() {
      var bar = this.querySelector('[data-pdp-stickybar]');
      var buy = this.querySelector('[data-pdp-buy]');
      if (!bar || !buy || !('IntersectionObserver' in window)) return;
      this.stickyObserver = new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      });
      this.stickyObserver.observe(buy);
    }

    /* ------------------------------ eventos ----------------------------- */

    handleClick(event) {
      var thumb = event.target.closest('[data-media-target]');
      if (thumb && this.contains(thumb)) {
        this.setActiveMedia(thumb.dataset.mediaTarget, true);
        return;
      }
      var stepper = event.target.closest('[data-quantity-change]');
      if (stepper && this.contains(stepper)) {
        this.stepQuantity(parseInt(stepper.dataset.quantityChange, 10) || 0);
        return;
      }
    }

    /* -------------------------------- zoom ------------------------------- */

    // Zoom em contexto: a imagem amplia dentro do próprio quadro.
    // Desktop (hover fino): segue o cursor, sem clique. Toque: um tap
    // amplia no ponto tocado (pan por arrasto), outro tap volta. Na
    // primeira ampliação a imagem sobe para a versão 2048px.
    upgradeZoomImage(item) {
      if (item.dataset.zoomUpgraded) return;
      var img = item.querySelector('img');
      if (!img || !item.dataset.zoomSrc) return;
      img.srcset = '';
      img.sizes = '';
      img.src = item.dataset.zoomSrc;
      item.dataset.zoomUpgraded = 'true';
    }

    setZoomOrigin(item, event) {
      var img = item.querySelector('img');
      if (!img) return;
      var rect = item.getBoundingClientRect();
      var x = ((event.clientX - rect.left) / rect.width) * 100;
      var y = ((event.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = Math.max(0, Math.min(100, x)) + '% ' + Math.max(0, Math.min(100, y)) + '%';
    }

    zoomIn(item, event) {
      this.upgradeZoomImage(item);
      if (event) this.setZoomOrigin(item, event);
      item.classList.add('is-zoomed');
      item.setAttribute('aria-pressed', 'true');
      item.style.touchAction = 'none'; // pan sem rolar a página
      var gallery = item.closest('[data-pdp-gallery]');
      if (gallery) gallery.classList.add('pdp-gallery--zooming');
    }

    zoomOut(item) {
      item.classList.remove('is-zoomed');
      item.setAttribute('aria-pressed', 'false');
      item.style.touchAction = '';
      var img = item.querySelector('img');
      if (img) img.style.transformOrigin = '';
      var gallery = item.closest('[data-pdp-gallery]');
      if (gallery) gallery.classList.remove('pdp-gallery--zooming');
    }

    // Desktop: SEGURAR o botão amplia (solta = volta) — nada automático no
    // hover. Toque: um tap amplia no ponto, outro volta. Teclado: Enter
    // alterna, Esc sai.
    bindZoom(item) {
      var self = this;

      item.addEventListener('pointerdown', function (event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        event.preventDefault(); // evita arrastar a imagem/selecionar
        self.zoomIn(item, event);
        // captura mantém o pan mesmo se o cursor sair do quadro segurando
        try {
          item.setPointerCapture(event.pointerId);
        } catch (error) {
          // ponteiro já inativo (ex.: eventos sintéticos) — zoom segue sem captura
        }
      });
      item.addEventListener('pointermove', function (event) {
        if (item.classList.contains('is-zoomed')) self.setZoomOrigin(item, event);
      });
      item.addEventListener('pointerup', function (event) {
        if (event.pointerType === 'mouse') {
          self.zoomOut(item);
          return;
        }
        // toque: alterna
        if (item.classList.contains('is-zoomed')) {
          self.zoomOut(item);
        } else {
          self.zoomIn(item, event);
        }
      });
      item.addEventListener('pointercancel', function () {
        self.zoomOut(item);
      });
      item.addEventListener('dragstart', function (event) {
        event.preventDefault();
      });
      item.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (item.classList.contains('is-zoomed')) {
            self.zoomOut(item);
          } else {
            self.zoomIn(item, null);
          }
        }
        if (event.key === 'Escape') self.zoomOut(item);
      });
    }

    handleChange(event) {
      var control = event.target;
      if (control.matches('[data-pdp-quantity-input]')) {
        this.clampQuantity(control);
        return;
      }
      if (!control.closest('[data-pdp-options]')) return;

      // Combined listings: valor pertence a outro produto → navegação plena
      var picked = control.tagName === 'SELECT' ? control.selectedOptions[0] : control;
      var ids = this.selectedOptionValueIds();
      if (picked && picked.dataset.productUrl) {
        window.location.href = picked.dataset.productUrl + '?option_values=' + ids.join(',');
        return;
      }
      this.updateFromSelection(ids);
    }

    /* ----------------------------- quantidade --------------------------- */

    quantityInput() {
      return this.querySelector('[data-pdp-quantity-input]');
    }

    clampQuantity(input) {
      var min = parseInt(input.min, 10) || 1;
      var step = parseInt(input.step, 10) || 1;
      var max = input.max ? parseInt(input.max, 10) : Infinity;
      var value = parseInt(input.value, 10);
      if (isNaN(value)) value = min;
      if (value < min) value = min;
      if (value > max) value = max;
      // alinha ao incremento a partir do mínimo
      value = min + Math.round((value - min) / step) * step;
      if (value > max) value -= step;
      if (value < min) value = min;
      input.value = value;
    }

    stepQuantity(direction) {
      var input = this.quantityInput();
      if (!input || !direction) return;
      var step = parseInt(input.step, 10) || 1;
      var value = parseInt(input.value, 10);
      if (isNaN(value)) value = parseInt(input.min, 10) || 1;
      input.value = value + direction * step;
      this.clampQuantity(input);
    }

    /* ------------------------- troca de variante ------------------------ */

    selectedOptionValueIds() {
      var ids = [];
      var fieldsets = this.querySelectorAll('[data-pdp-options] fieldset[data-option-position]');
      Array.prototype.forEach.call(fieldsets, function (fieldset) {
        var select = fieldset.querySelector('[data-option-select]');
        if (select) {
          ids.push(select.value);
          return;
        }
        var checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) ids.push(checked.value);
      });
      return ids;
    }

    updateFromSelection(ids) {
      if (!ids.length) return;
      var key = ids.join(',');
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.pendingKey = key;
      this.setBusy(true);
      this.announce(this.dataset.textLoading || '');

      var url = new URL(this.dataset.productUrl, window.location.origin);
      url.searchParams.set('option_values', key);
      url.searchParams.set('section_id', this.dataset.sectionId);

      var self = this;
      fetch(url.toString(), { signal: this.abortController.signal })
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(function (html) {
          if (!self.isConnected || self.pendingKey !== key) return; // resposta antiga
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('product-page');
          if (!fresh) throw new Error('section');
          self.applyUpdate(fresh, key);
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          // Falha de rede: mantém o estado atual e informa educadamente
          if (self.isConnected && self.pendingKey === key) {
            self.announce(self.dataset.textError || '');
          }
        })
        .finally(function () {
          if (self.isConnected && self.pendingKey === key) self.setBusy(false);
        });
    }

    setBusy(busy) {
      var info = this.querySelector('[data-pdp-info]');
      if (!info) return;
      if (busy) {
        info.setAttribute('aria-busy', 'true');
      } else {
        info.removeAttribute('aria-busy');
      }
    }

    applyUpdate(fresh, key) {
      this.syncFragment(fresh, '[data-pdp-price]');
      this.syncFragment(fresh, '[data-pdp-sku]');
      this.syncFragment(fresh, '[data-pdp-availability]');
      this.syncFragment(fresh, '[data-pdp-presentation]');
      this.syncFragment(fresh, '[data-pdp-stickybar-price]');
      this.syncAccordions(fresh, '[data-pdp-nutrition]');
      this.syncAccordions(fresh, '[data-pdp-allergens]');
      this.syncOptions(fresh);
      this.syncQuantity(fresh);
      this.syncBuy(fresh);
      this.syncStickyCta(fresh);

      var mediaId = fresh.dataset.featuredMedia;
      if (mediaId) this.setActiveMedia(mediaId, true);

      this.updateUrl(fresh.dataset.variantId, key);
      this.announceCommercial();
    }

    syncStickyCta(fresh) {
      var cta = this.querySelector('[data-pdp-stickybar-cta]');
      var next = fresh.querySelector('[data-pdp-stickybar-cta]');
      if (cta && next) cta.disabled = next.disabled;
    }

    // Conteúdo técnico por sabor (tabela/ingredientes/alergênicos):
    // sincroniza o fragmento e preserva o estado aberto/fechado dos
    // acordeões através da troca
    syncAccordions(fresh, selector) {
      var local = this.querySelector(selector);
      var next = fresh.querySelector(selector);
      if (!local || !next) return;
      var openStates = {};
      Array.prototype.forEach.call(local.querySelectorAll('details[data-pdp-accordion]'), function (details) {
        openStates[details.dataset.pdpAccordion] = details.open;
      });
      local.innerHTML = next.innerHTML;
      local.hidden = next.hidden;
      Array.prototype.forEach.call(local.querySelectorAll('details[data-pdp-accordion]'), function (details) {
        if (Object.prototype.hasOwnProperty.call(openStates, details.dataset.pdpAccordion)) {
          details.open = openStates[details.dataset.pdpAccordion];
        }
      });
    }

    // Fragments não interativos: troca innerHTML/estado, preserva o nó
    // (e os shopify_attributes do editor de tema)
    syncFragment(fresh, selector) {
      var local = this.querySelector(selector);
      var next = fresh.querySelector(selector);
      if (!local || !next) return;
      local.innerHTML = next.innerHTML;
      local.hidden = next.hidden;
      if (next.dataset.state) {
        local.dataset.state = next.dataset.state;
      }
    }

    syncOptions(fresh) {
      var self = this;

      // Valor selecionado exibido em cada legend
      var fieldsets = this.querySelectorAll('[data-pdp-options] fieldset[data-option-position]');
      Array.prototype.forEach.call(fieldsets, function (fieldset) {
        var freshFieldset = fresh.querySelector(
          'fieldset[data-option-position="' + fieldset.dataset.optionPosition + '"]'
        );
        if (!freshFieldset) return;
        var label = fieldset.querySelector('[data-option-selected]');
        var freshLabel = freshFieldset.querySelector('[data-option-selected]');
        if (label && freshLabel) label.textContent = freshLabel.textContent;
      });

      // Radios: disponibilidade visual + texto para leitores de tela
      var radios = this.querySelectorAll('[data-pdp-options] input[type="radio"][data-option-value-id]');
      Array.prototype.forEach.call(radios, function (input) {
        var next = fresh.querySelector('input[data-option-value-id="' + input.dataset.optionValueId + '"]');
        if (!next) return;
        input.dataset.available = next.dataset.available;
        var label = input.nextElementSibling;
        var freshLabel = next.nextElementSibling;
        if (!label || !freshLabel) return;
        label.classList.toggle('pdp-option__label--unavailable', next.dataset.available === 'false');
        var status = label.querySelector('[data-option-status]');
        var freshStatus = freshLabel.querySelector('[data-option-status]');
        if (status && freshStatus) status.textContent = freshStatus.textContent;

        // Em produto de duas opções o descritor e a miniatura de cada sabor
        // dependem do tamanho selecionado — vêm resolvidos do servidor a cada
        // troca. Sem copiá-los aqui, o card continuaria anunciando a
        // embalagem anterior.
        var detail = label.querySelector('.pdp-option__value-detail');
        var freshDetail = freshLabel.querySelector('.pdp-option__value-detail');
        if (detail && freshDetail) detail.textContent = freshDetail.textContent;

        var thumb = label.querySelector('.pdp-option__thumb img');
        var freshThumb = freshLabel.querySelector('.pdp-option__thumb img');
        if (thumb && freshThumb && thumb.src !== freshThumb.src) thumb.src = freshThumb.src;
      });

      // Selects: texto das opções (sufixo de indisponibilidade)
      var options = this.querySelectorAll('[data-pdp-options] option[data-option-value-id]');
      Array.prototype.forEach.call(options, function (option) {
        var next = fresh.querySelector('option[data-option-value-id="' + option.dataset.optionValueId + '"]');
        if (!next) return;
        option.dataset.available = next.dataset.available;
        option.textContent = next.textContent;
      });
    }

    syncQuantity(fresh) {
      var input = this.quantityInput();
      var next = fresh.querySelector('[data-pdp-quantity-input]');
      if (input && next) {
        input.min = next.min;
        input.step = next.step;
        if (next.max) {
          input.max = next.max;
        } else {
          input.removeAttribute('max');
        }
        this.clampQuantity(input);
      }
      var rules = this.querySelector('[data-pdp-quantity-rules]');
      var nextRules = fresh.querySelector('[data-pdp-quantity-rules]');
      if (rules && nextRules) {
        rules.textContent = nextRules.textContent;
        rules.hidden = nextRules.hidden;
      }
    }

    syncBuy(fresh) {
      var local = this.querySelector('[data-pdp-buy]');
      var next = fresh.querySelector('[data-pdp-buy]');
      if (!local || !next) return;

      local.dataset.state = next.dataset.state || '';

      var input = local.querySelector('[data-pdp-variant-input]');
      var nextInput = next.querySelector('[data-pdp-variant-input]');
      if (input && nextInput) {
        input.value = nextInput.value;
        input.disabled = nextInput.disabled;
      }

      var submit = local.querySelector('[data-pdp-submit]');
      var nextSubmit = next.querySelector('[data-pdp-submit]');
      if (submit && nextSubmit) {
        submit.disabled = nextSubmit.disabled;
        var text = submit.querySelector('[data-pdp-submit-text]');
        var nextText = nextSubmit.querySelector('[data-pdp-submit-text]');
        if (text && nextText) text.textContent = nextText.textContent.trim();
      }

      // O aviso de plano de compra só existe no HTML quando a variante o exige,
      // para não deixar o texto no código-fonte de toda página. Como a troca de
      // variante pode entrar ou sair desse estado, aqui ele é inserido ou
      // removido — não apenas escondido.
      var note = local.querySelector('[data-pdp-plan-note]');
      var nextNote = next.querySelector('[data-pdp-plan-note]');
      if (nextNote && !note) {
        var live = local.querySelector('[data-pdp-live]');
        local.insertBefore(nextNote.cloneNode(true), live || null);
      } else if (note && !nextNote) {
        note.parentNode.removeChild(note);
      } else if (note && nextNote) {
        note.textContent = nextNote.textContent;
      }
    }

    updateUrl(variantId, key) {
      if (!window.history || !window.history.replaceState) return;
      var params = new URLSearchParams(window.location.search);
      if (variantId) {
        params.set('variant', variantId);
        params.delete('option_values');
      } else {
        params.set('option_values', key);
        params.delete('variant');
      }
      var query = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (query ? '?' + query : ''));
    }

    /* ------------------------------ anúncios ---------------------------- */

    announceCommercial() {
      var parts = [];
      var price = this.querySelector('[data-pdp-price]');
      if (price && !price.hidden) parts.push(price.textContent.replace(/\s+/g, ' ').trim());
      var availability = this.querySelector('[data-pdp-availability]');
      if (availability) parts.push(availability.textContent.replace(/\s+/g, ' ').trim());
      this.announce(parts.join(' — '));
    }

    announce(text) {
      var live = this.querySelector('[data-pdp-live]');
      if (live) live.textContent = text;
    }
  }

  customElements.define('product-page', ProductPage);
})();
